import { Component, OnDestroy, OnInit } from '@angular/core'
import { Observable, Subscription, forkJoin } from 'rxjs'
import { ActivatedRoute, Router } from '@angular/router'
import { MessageService } from 'primeng/api'
import { ViewChild } from '@angular/core'

import { ApplyPreviewDialogComponent, PreviewMode } from '@modules/private/lease/components/lease-enforceability/apply-preview-dialog/apply-preview-dialog.component'
import { AllocatableLease } from '@modules/private/payment/components/lease-distribution-selector/lease-distribution-selector.component'
import { FilaPago, PaymentApplicationCancelRequest } from '@modules/private/payment/components/payments-table/payments-table.component'
import { AttentionGroup, TrayGroup } from '@modules/private/payment/components/payment-adjust-modal/payment-adjust-modal.component'
import { toAllocatableLeases } from '@modules/private/payment/components/lease-distribution-selector/lease-allocation.util'
import { IPaymentCorrection, PossibleDuplicateDetail } from '@modules/private/payment/data/interfaces/ipayment.interface'
import { PaymentsTableComponent } from '@modules/private/payment/components/payments-table/payments-table.component'
import { ApiApplicationService } from '@modules/private/application/data/services/api/api-application.service'
import { CobranzaShellService } from '@modules/private/cobranza/data/services/view/cobranza-shell.service'
import { ApiPaymentService } from '@modules/private/payment/data/services/api/api-payment.service'
import { PAYMENT_STATUS_LABELS } from '@modules/private/payment/data/constants/payment.constants'
import { ApiWalletService } from '@modules/private/wallet/data/services/api/api-wallet.service'
import { PaymentService } from '@modules/private/payment/data/services/view/payment.service'
import { ApiLeaseService } from '@modules/private/lease/data/services/api/api-lease.service'
import { pagosPorAtender } from '@modules/private/payment/data/constants/pagos-por-atender'
import { PaymentRow } from '@modules/private/lease/data/interfaces/lease-ledger.interface'
import { grupoValido } from '@modules/private/payment/data/constants/grupos-por-atender'
import { extractApiErrors } from '@data/utils/api-error.util'

/**
 * Medio centavo. El campo de monto admite dos decimales, así que dos cifras que difieren en menos que
 * esto son la misma cifra con ruido de punto flotante, no una corrección que mandar.
 */
const AMOUNT_TOLERANCE = 0.005
/** Los límites del motivo de «Editar pago»: mismo nombre y valor que en el DTO del API (`CorrectPaymentDto.reason`). */
const CORRECTION_REASON_MIN_LENGTH = 10
const CORRECTION_REASON_MAX_LENGTH = 500
/**
 * El tope del motivo para registrar un posible duplicado en el alta: mismo valor que el DTO del API
 * (`CreatePaymentDto.duplicateReason`). A diferencia de «Editar pago» no hay mínimo: el API solo exige
 * que no venga vacío, igual que el alta de la pestaña Exigibilidad.
 */
const DUPLICATE_REASON_MAX_LENGTH = 500

@Component({
  selector: 'app-payment-list',
  templateUrl: './payment-list.component.html',
  styleUrls: ['./payment-list.component.scss'],
  providers: [MessageService],
})
export class PaymentListComponent implements OnInit, OnDestroy {
  /**
   * Qué enseña: el dinero del mes, o la cola de excepciones.
   *
   * Sale del `data` de su RUTA —`/cobranza/dinero` o `/cobranza/por-atender`— y no de un `@Input`:
   * desde que Cobranza es un cascarón con `<router-outlet>`, a este componente lo crea el enrutador
   * y su padre no puede pasarle entradas. Nunca es estado de este componente.
   */
  lens: 'todos' | 'atender' = 'todos'

  /** La tabla propia de Dinero, para poder recargarla tras un ajuste. */
  @ViewChild('tablaPagos') tablaPagos?: PaymentsTableComponent

  createEntity = false
  action = 'CREATE'
  /** El alta va en camino: el método y los dos botones de guardar lo respetan, así un doble clic manda un solo POST. */
  createSaving = false
  /** Evidencia del 409 `POSSIBLE_DUPLICATE` en el alta. Mientras exista, guardar exige un motivo. */
  createDuplicate: PossibleDuplicateDetail | null = null
  createDuplicateReason = ''
  readonly createDuplicateReasonMaxLength = DUPLICATE_REASON_MAX_LENGTH
  /**
   * Cliente, monto, clave y fecha con que se ENVIÓ el alta que devolvió la evidencia. Si la captura se
   * aparta de ellos, la evidencia ya es la de otros valores y confirmarla registraría un pago que el
   * API no revisó. Cuenta y método no entran: el API no los usa para buscar gemelos.
   */
  private createDuplicateFingerprint: string | null = null
  /**
   * `entity` vive en `PaymentService`, que es raíz: la suscripción sobrevive al diálogo y a esta vista
   * si no se cierra a mano. Se cierra al ocultar el diálogo y en `ngOnDestroy`.
   */
  private createEntityChanges?: Subscription

  // Pestaña "Por atender": remanentes, posibles duplicados y anulados
  /**
   * Qué grupo enseña la bandeja. Sale de `?grupo=` y lo eligen las fichas de la barra de Cobranza.
   *
   * Se tipa `TrayGroup` y NO `AttentionGroup`: el segundo incluye `'listado'`, que es el pseudogrupo
   * que usa el modal cuando se anula desde la tabla de Dinero y no existe como bandeja.
   */
  attentionGroup: TrayGroup = 'remanentes'
  attentionLoading = false
  attentionPreviewing = false
  attentionSaving = false
  attentionData: any = null
  /** Para no volver a pedir el listado cada vez que se cambia de pestaña. */
  private attentionLoaded = false

  // Aplicar un pago desde Dinero: el mismo diálogo y el mismo motor que la pestaña Exigibilidad
  showApply = false
  applyPayments: PaymentRow[] = []
  applyLeaseId = ''
  applyClientLeases: AllocatableLease[] = []
  applyNextToCollect: number | null = null
  applyNextToCollectDate: string | null = null
  applyPreviewing = false
  applySaving = false
  /** Mientras se buscan los arrendamientos del cliente: el botón no puede abrir dos veces. */
  preparingApply = false
  /**
   * El pago recién registrado, a la espera de que el diálogo del alta TERMINE de cerrarse.
   *
   *! Abrir el de aplicar mientras el del alta todavía anima su salida no funciona: PrimeNG cierra el
   *! recién abierto al acabar la animación del otro, y el diálogo parpadea y desaparece sin aviso.
   *! Se descubrió en el recorrido: el pago quedaba registrado y el diálogo de aplicar nunca se veía.
   */
  private pendingApply: FilaPago | null = null

  /**
   * El mismo diálogo sirve para aplicar y para cancelar una aplicación (F-045): lo que cambia es el
   * modo, y con él el endpoint al que van la previsualización y la confirmación.
   */
  previewMode: PreviewMode = 'APPLY'
  /** La aplicación que se va a cancelar. `null` mientras el diálogo esté en modo aplicar. */
  cancelTarget: { applicationId: string; n: number | null; letters: number[]; amount: number } | null = null

  // Corregir monto o clave de UN pago (F-045)
  showEdit = false
  editTarget: FilaPago | null = null
  editAmount: number | null = null
  editTrackingKey = ''
  editReason = ''
  editSaving = false
  /** Rechazo del backend que se enseña dentro del diálogo: el operador lo corrige sin perder lo capturado. */
  editError = ''
  /** Evidencia del 409 `POSSIBLE_DUPLICATE`. Mientras exista, guardar exige confirmarlo. */
  editDuplicate: PossibleDuplicateDetail | null = null
  /** La clave tal como venía: la fila pinta «—» cuando no hay, y eso no es una clave que comparar. */
  private editOriginalKey = ''
  //* La plantilla lee los mismos límites que valida el getter: una sola fuente.
  readonly editReasonMinLength = CORRECTION_REASON_MIN_LENGTH
  readonly editReasonMaxLength = CORRECTION_REASON_MAX_LENGTH

  // Modal de ajuste de UN pago
  showAdjustModal = false
  selectedAttentionRow: any = null
  selectedAttentionGroup: AttentionGroup = 'remanentes'

  constructor(
    public apiPaymentService: ApiPaymentService,
    public paymentService: PaymentService,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private readonly apiLease: ApiLeaseService,
    private readonly apiWallet: ApiWalletService,
    private readonly apiApplication: ApiApplicationService,
    private readonly shell: CobranzaShellService,
  ) {}

  ngOnInit(): void {
    this.lens = (this.route.snapshot.data?.['lens'] as 'todos' | 'atender') ?? 'todos'
    //* El grupo de la bandeja vive en la dirección, y lo eligen las fichas de la barra de Cobranza.
    //* Se ESCUCHA: cambiar de grupo no recrea este componente, solo cambia la query.
    this.route.queryParamMap.subscribe((q) => (this.attentionGroup = grupoValido(q.get('grupo'))))
    //* La vista la manda la ruta, así que si nace en la bandeja hay que pedir sus datos aquí: ya no
    //* hay cambio de pestaña que los dispare.
    if (this.lens === 'atender' && !this.attentionLoaded) this.loadAttention()
  }

  ngOnDestroy(): void {
    this.createEntityChanges?.unsubscribe()
  }

  hideDialog() {
    this.createEntity = false
  }

  mainActions(flow: string) {
    switch (flow) {
      case 'CREATE':
        this.paymentService.newEntity()
        this.createSaving = false
        this.resetCreateDuplicate()
        this.watchCreateEntity()
        this.createEntity = true
        break
      case 'CREATE_MANY':
        //* El asistente vive en un modal de Cobranza y se enciende por la dirección, no por una
        //* bandera: así «atrás» lo cierra y un lote a medias se puede compartir por enlace.
        this.router.navigate([], { relativeTo: this.route, queryParams: { carga: 'nuevo' }, queryParamsHandling: 'merge' })
        break
      default:
        console.log(flow)
        break
    }
  }

  // ── Pestaña "Por atender" ──

  /** Lo que de verdad está pendiente. La MISMA cuenta que el aviso del encabezado de Cobranza. */
  get attentionTotal(): number {
    return pagosPorAtender(this.attentionData?.resumen)
  }

  /**
   * Las pestañas que de verdad se pintan, EN ORDEN. Tiene que replicar exactamente las condiciones
   * de la plantilla: PrimeNG numera solo las renderizadas.
   *
   * Antes esto se decidía con `e.index === 1` contra la posición literal. Funcionaba de milagro,
   * porque la única pestaña condicional está al final; en cuanto se meta una delante —o se gatee una
   * de en medio, que es justo lo que pide el permiso por pestaña— el índice se corre y «Por atender»
   * deja de pedir sus datos EN SILENCIO: sin error, sin aviso, solo vacía. El mismo fallo ya ocurrió
   * en el detalle del arrendamiento y allí se resolvió así (`lease-detail.component.ts`).
   */

  loadAttention(): void {
    this.attentionLoaded = true
    this.attentionLoading = true
    this.apiPaymentService.getNeedingAttention().subscribe((r) => {
      this.attentionLoading = false
      if (r.error) {
        this.messageService.add({ severity: 'error', summary: 'Pagos por atender', detail: r.msg, life: 6000 })
        return
      }
      this.attentionData = r.data
    })
  }

  openAdjust(e: { row: any; group: AttentionGroup }): void {
    this.selectedAttentionRow = e.row
    this.selectedAttentionGroup = e.group
    this.showAdjustModal = true
  }

  /** Estado, aplicaciones y bitácora del pago: lo que el modal usa para explicar el ajuste. */
  loadContext(paymentId: string, modal: any): void {
    this.apiPaymentService.getPaymentContext(paymentId).subscribe((r) => {
      modal.setContext(r.error ? null : r.data)
      if (r.error) this.messageService.add({ severity: 'warn', summary: 'Información del pago', detail: r.msg, life: 5000 })
    })
  }

  /** Corre el dryRun: el backend calcula todo y aborta la transacción, así que no escribe nada. */
  previewAdjust(e: { paymentId: string; payload: any }, modal: any): void {
    this.attentionPreviewing = true
    this.apiPaymentService.adjustPayment(e.paymentId, e.payload, true).subscribe((r) => {
      this.attentionPreviewing = false
      if (r.error) {
        modal.setPreview(null, r.msg || 'No se pudo calcular el impacto del ajuste.')
        return
      }
      modal.setPreview(r.data)
    })
  }

  applyAdjust(e: { paymentId: string; payload: any }, modal: any): void {
    this.attentionSaving = true
    this.apiPaymentService.adjustPayment(e.paymentId, e.payload, false).subscribe((r) => {
      this.attentionSaving = false
      if (r.error) {
        this.messageService.add({ severity: 'error', summary: 'No se pudo aplicar', detail: r.msg, life: 8000 })
        //* Si el pago cambió entre la previsualización y el guardado, el backend responde 409: hay
        //* que volver a calcular contra el estado nuevo en lugar de reintentar a ciegas.
        modal.invalidatePreview()
        return
      }
      this.messageService.add({ severity: 'success', summary: 'Pago ajustado', detail: this.adjustSummary(r.data), life: 6000 })
      //* Se cierra el modal y se recarga la bandeja: el pago ajustado ya no debería aparecer ahí.
      modal.hide()
      this.loadAttention()
      this.tablaPagos?.recargar()
    })
  }

  private adjustSummary(data: any): string {
    const money = (n: number) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n || 0)
    switch (data?.operation) {
      case 'VOID_DUPLICATE':
        return 'El pago quedó fuera de circulación.'
      case 'CLOSE_REMAINDER':
        return `Se cerró un remanente de ${money(data?.before?.pendingToApply)}.`
      case 'REVERT':
        return data?.before?.deleted && !data?.after?.deleted
          ? `El pago volvió a circulación con ${money(data?.after?.pendingToApply)} por aplicar.`
          : 'El ajuste quedó deshecho: el pago volvió a su estado anterior.'
      default:
        return 'El ajuste se aplicó correctamente.'
    }
  }

  /**
   * Anular desde el listado. La fila de aquí no tiene la misma forma que la de la bandeja, así que
   * se traduce; los saldos reales los trae el `context` en cuanto abre el modal.
   */
  openVoidFromList(fila: any): void {
    this.selectedAttentionRow = {
      _id: fila._id,
      trackingKey: fila.claveRastreo ?? fila.trackingKey,
      cliente: fila.cliente ?? fila.client?.name ?? null,
      monto: fila.monto ?? fila.amount,
      fecha: fila.fecha ?? fila.date,
      //* Lo aplicado sale del LIBRO, no de `amount - pendingToApply`: ese campo es una copia guardada
      //* que se desfasa, y el modal decide qué operaciones ofrecer a partir de estas cifras.
      aplicado: fila.aplicado ?? 0,
      remanente: fila.sinAplicar ?? fila.pendingToApply ?? 0,
      status: fila.status,
    }
    this.selectedAttentionGroup = 'listado'
    this.showAdjustModal = true
  }

  /** Confirmar el posible duplicado exige motivo: el API lo rechaza vacío y queda en la bitácora del pago. */
  get canConfirmCreateDuplicate(): boolean {
    return !!this.createDuplicateReason.trim() && !this.createSaving && this.paymentService.entity.valid
  }

  saveForm(confirmDuplicate = false): void {
    //! La guarda vive en el método y no solo en el botón: un doble clic llega antes de que el botón se
    //! deshabilite, y el segundo POST chocaría con el primero como su propio duplicado.
    if (this.createSaving) return
    if (confirmDuplicate && !this.canConfirmCreateDuplicate) return
    this.createSaving = true
    const sentFingerprint = this.createFingerprint()
    //* El alta pasa por `PaymentService.save()`, el mismo camino que usa la pestaña Exigibilidad, para
    //* que el 409 de posible duplicado se comporte igual en los dos sitios. El actor sale del token (F22).
    const extra = confirmDuplicate ? { confirmDuplicate: true, duplicateReason: this.createDuplicateReason.trim() } : {}
    this.paymentService.save(extra).subscribe((r) => {
      this.createSaving = false
      if (r.error && r.code === 'POSSIBLE_DUPLICATE') {
        //* Evidencia, no veredicto: se pinta dentro del diálogo y la persona decide si lo registra de
        //* todos modos. Antes se mandaba a la pestaña Exigibilidad, que pide abrir el arrendamiento.
        this.createDuplicate = r.detail ?? { twins: [], activeLeases: 0 }
        this.createDuplicateFingerprint = sentFingerprint
        //* Si la captura cambió mientras viajaba la petición, la evidencia ya nace vieja.
        this.discardStaleCreateDuplicate()
        return
      }
      if (r.error) {
        this.messageService.add({ severity: 'error', summary: 'Error al crear el pago', detail: r.msg })
        return
      }
      const cliente = this.paymentService.entity?.getRawValue?.()
      this.resetCreateDuplicate()
      this.hideDialog()
      //! `getAll()` devuelve un observable: sin suscribirse no sale la petición y la tabla se quedaba
      //! como estaba tras crear el pago. Se recarga la tabla propia, que es la que pinta las filas.
      this.tablaPagos?.recargar()
      //* El embudo del mes también cuenta este dinero: sin avisarle al cascarón, «Entró» no se movía.
      this.shell.refresh()
      this.messageService.add({ severity: 'success', summary: 'Pago registrado', detail: 'Ahora puedes aplicarlo a las letras del cliente.' })
      //* Se ofrece aplicarlo enseguida, como en Exigibilidad. Si se cierra el diálogo, el pago queda
      //* «Sin aplicar» y el botón de su fila sigue ahí.
      const nuevoId: string | undefined = r.data?._id
      if (nuevoId && cliente?.client) {
        this.pendingApply = { _id: nuevoId, clienteId: String(cliente.client), claveRastreo: cliente.trackingKey, sinAplicar: Number(cliente.amount) || 0, fecha: cliente.date } as FilaPago
      }
    })
  }

  /** El diálogo del alta ya se cerró del todo: ahora sí se puede abrir el de aplicar. */
  onCreateDialogHidden(): void {
    //* Cerrar descarta la evidencia: reabrir arranca limpio, y la suscripción no sigue escuchando un
    //* formulario que ya nadie ve.
    this.resetCreateDuplicate()
    this.createEntityChanges?.unsubscribe()
    this.createEntityChanges = undefined
    const fila = this.pendingApply
    this.pendingApply = null
    if (fila) this.openApply(fila)
  }

  /** Escucha la captura del alta para tirar la evidencia en cuanto deje de corresponder a lo enviado. */
  private watchCreateEntity(): void {
    this.createEntityChanges?.unsubscribe()
    this.createEntityChanges = this.paymentService.entity.valueChanges.subscribe(() => this.discardStaleCreateDuplicate())
  }

  private discardStaleCreateDuplicate(): void {
    if (this.createDuplicateFingerprint !== null && this.createFingerprint() !== this.createDuplicateFingerprint) this.resetCreateDuplicate()
  }

  /**
   * Los cuatro campos con que el API busca gemelos. `getRawValue()` porque el cliente puede venir
   * deshabilitado; `JSON.stringify` deja la fecha en ISO, así que dos `Date` iguales cuentan como iguales.
   */
  private createFingerprint(): string {
    const { client, amount, trackingKey, date } = this.paymentService.entity.getRawValue()

    return JSON.stringify([client, amount, trackingKey, date])
  }

  private resetCreateDuplicate(): void {
    this.createDuplicate = null
    this.createDuplicateReason = ''
    this.createDuplicateFingerprint = null
  }

  /**
   * Aplica un pago desde Dinero.
   *
   * Cobranza no reimplementa el motor (frontera con Exigibilidad, apuntes §3): abre el MISMO diálogo
   * que la pestaña del arrendamiento, que llama al mismo `POST /applications/create`. Lo único que hace
   * aquí es lo que allá ya viene resuelto: saber a qué arrendamientos puede ir el dinero (D3).
   *
   * - Sin arrendamientos activos no hay a dónde aplicar: el dinero queda disponible y se dice.
   * - Con uno, ese. Con varios, el diálogo enseña el selector de reparto.
   *
   * La fila del pago se toma del libro mayor del arrendamiento: su `pendingToApply` es la cifra con la
   * que trabaja el motor. Si no aparece ahí, se arma con la de la tabla.
   */
  openApply(fila: FilaPago): void {
    if (!fila?.clienteId || this.preparingApply) return
    this.preparingApply = true
    this.previewMode = 'APPLY'
    this.cancelTarget = null
    forkJoin({ leases: this.apiLease.getActiveByClient(fila.clienteId), settings: this.apiWallet.getSettings() }).subscribe(({ leases, settings }) => {
      const moraEnabled = !settings?.error && !!(settings?.data as any)?.moraEnabled
      this.applyClientLeases = toAllocatableLeases(leases, moraEnabled)
      if (!this.applyClientLeases.length) {
        this.preparingApply = false
        this.messageService.add({
          severity: 'warn',
          summary: 'Sin arrendamientos activos',
          detail: 'El cliente no tiene arrendamientos activos: el pago queda disponible para aplicarse después.',
          life: 8000,
        })
        return
      }
      this.applyLeaseId = this.applyClientLeases[0].leaseId
      const respaldo = { _id: fila._id, trackingKey: fila.claveRastreo, pendingToApply: fila.sinAplicar, paidOn: fila.fecha } as PaymentRow
      this.apiPaymentService.getLedger(this.applyLeaseId).subscribe((r) => {
        this.preparingApply = false
        const ledger = r.error ? null : r.data
        const real = ledger?.unapplied?.find((p) => p._id === fila._id)
        this.applyPayments = [real ?? respaldo]
        this.applyNextToCollect = ledger?.nextToCollect ?? null
        this.applyNextToCollectDate = this.applyNextToCollect == null ? null : ((ledger?.periods ?? []).find((p) => p.n === this.applyNextToCollect)?.date ?? null)
        this.showApply = true
      })
    })
  }

  /**
   * Cancelar UNA aplicación desde Dinero (F-045, D-2).
   *
   * Es el mismo diálogo y el mismo endpoint que la pestaña Exigibilidad (`cancelWithReason`): previsualiza
   * con dryRun, exige motivo y manda la huella de estado. Desde aquí no hay libro mayor cargado, así que
   * el pago va con lo único que el diálogo enseña en este modo: su id y su clave.
   */
  openCancelApplication(e: PaymentApplicationCancelRequest): void {
    this.previewMode = 'CANCEL'
    this.applyPayments = [{ _id: e.paymentId, trackingKey: e.trackingKey } as PaymentRow]
    //* `n` es la primera letra y queda de respaldo, como en Exigibilidad: el diálogo prefiere `letters`.
    this.cancelTarget = { applicationId: e.applicationId, n: e.letters[0] ?? null, letters: e.letters, amount: e.amount }
    //* Sin arrendamientos del cliente el diálogo no pinta el selector de reparto, que en cancelar no aplica.
    this.applyClientLeases = []
    this.applyNextToCollect = null
    this.applyNextToCollectDate = null
    this.showApply = true
  }

  /** Corre el dryRun: el backend calcula todo con el mismo código y aborta la transacción. */
  previewInDialog(e: { mode: PreviewMode; applicationId?: string; payload: any }, modal: ApplyPreviewDialogComponent): void {
    this.applyPreviewing = true
    this.dialogRequest(e, true).subscribe((r) => {
      this.applyPreviewing = false
      if (r.error) {
        modal.setPreview(null, r.msg || 'No se pudo calcular el impacto.')
        return
      }
      modal.setPreview(r.data)
    })
  }

  confirmInDialog(e: { mode: PreviewMode; applicationId?: string; payload: any }, modal: ApplyPreviewDialogComponent): void {
    this.applySaving = true
    this.dialogRequest(e, false).subscribe((r) => {
      this.applySaving = false
      const cancelling = e.mode === 'CANCEL'
      if (r.error) {
        this.messageService.add({ severity: 'error', summary: cancelling ? 'No se pudo cancelar' : 'No se pudo aplicar', detail: r.msg, life: 8000 })
        //* Huella caducada: el arrendamiento cambió entre previsualizar y confirmar; se recalcula.
        modal.invalidatePreview()
        return
      }
      modal.hide()
      this.messageService.add({ severity: 'success', summary: cancelling ? 'Aplicación cancelada' : 'Pago aplicado', detail: this.dialogSummary(cancelling, r.data), life: 6000 })
      //* La tabla y el embudo: cancelar devuelve dinero a «sin aplicar» igual que aplicar lo quita.
      this.tablaPagos?.recargar()
      this.shell.refresh()
    })
  }

  /** A qué endpoint va cada modo. Cancelar exige el id de la aplicación; aplicar va con el payload. */
  private dialogRequest(e: { mode: PreviewMode; applicationId?: string; payload: any }, dryRun: boolean): Observable<{ error: boolean; msg: string; data: any }> {
    return e.mode === 'CANCEL' ? this.apiApplication.cancelWithReason(e.applicationId as string, e.payload, dryRun) : this.apiApplication.createPreview(e.payload, dryRun)
  }

  private dialogSummary(cancelling: boolean, data: any): string {
    if (cancelling) return `${data?.reopened?.length ?? 0} letra(s) reabierta(s)`
    const plan: Array<{ resulting?: { status?: string } }> = data?.plan ?? []
    const cubiertas = plan.filter((p) => p.resulting?.status === 'PAID').length

    return `${plan.length} letra(s) tocada(s) · ${cubiertas} cubierta(s)`
  }

  // ── Corregir monto o clave (F-045) ──

  openEdit(row: FilaPago): void {
    this.editTarget = row
    this.editAmount = row.monto
    this.editOriginalKey = row.claveRastreo === '—' ? '' : row.claveRastreo
    this.editTrackingKey = this.editOriginalKey
    this.editReason = ''
    this.editError = ''
    this.editDuplicate = null
    this.showEdit = true
  }

  /**
   * El monto solo se corrige si el pago no tiene aplicaciones vivas (D-1, regla A): con dinero ya
   * repartido, cambiarlo dejaría el libro de aplicaciones contando otra cifra. Lo aplicado sale del
   * LIBRO (`aplicado`), no de `amount - pendingToApply`. El backend lo vuelve a comprobar (400).
   */
  get editAmountLocked(): boolean {
    return (this.editTarget?.aplicado ?? 0) > 0
  }

  /** Solo lo que cambió: el contrato rechaza un cuerpo sin cambios, y mandar de más ensucia la bitácora. */
  get editChanges(): Pick<IPaymentCorrection, 'amount' | 'trackingKey'> {
    const changes: Pick<IPaymentCorrection, 'amount' | 'trackingKey'> = {}
    if (!this.editTarget) return changes
    const amount = Number(this.editAmount)
    if (!this.editAmountLocked && this.editAmount !== null && Math.abs(amount - this.editTarget.monto) > AMOUNT_TOLERANCE) changes.amount = amount
    const key = this.editTrackingKey.trim()
    if (key !== this.editOriginalKey) changes.trackingKey = key

    return changes
  }

  /** Un motivo de dos palabras no le explica nada a quien lea la bitácora después. */
  get editReasonValid(): boolean {
    return this.editReasonMissing === 0
  }

  /** Cuántos caracteres le faltan al motivo para el mínimo. La plantilla lo dice con texto, no solo en rojo. */
  get editReasonMissing(): number {
    return Math.max(0, CORRECTION_REASON_MIN_LENGTH - this.editReason.trim().length)
  }

  get editReasonMissingText(): string {
    return this.editReasonMissing === 1 ? 'Falta 1 carácter' : `Faltan ${this.editReasonMissing} caracteres`
  }

  /** Solo cuenta como error cuando ya se empezó a escribir: un campo vacío recién abierto no es un fallo. */
  get editReasonInvalid(): boolean {
    return this.editReason.length > 0 && !this.editReasonValid
  }

  /** El `status` del gemelo en español: el contrato lo manda tal cual se guarda (APPLIED, PENDING…). */
  twinStatusLabel(status: string): string {
    return PAYMENT_STATUS_LABELS[status] || status || '—'
  }

  get canSaveEdit(): boolean {
    const changes = this.editChanges
    const hasChanges = changes.amount !== undefined || changes.trackingKey !== undefined
    //* Una clave vacía no es una corrección: el contrato la exige no vacía.
    const keyValid = changes.trackingKey === undefined || changes.trackingKey.length > 0
    const amountValid = changes.amount === undefined || changes.amount > 0

    return hasChanges && keyValid && amountValid && this.editReasonValid && !this.editSaving
  }

  /** Cualquier cambio en la captura tira la evidencia del gemelo: era la de otros valores. */
  onEditInput(): void {
    this.editDuplicate = null
    this.editError = ''
  }

  saveEdit(confirmDuplicate = false): void {
    if (!this.editTarget || !this.canSaveEdit) return
    const dto: IPaymentCorrection = { ...this.editChanges, reason: this.editReason.trim() }
    //* El motivo del duplicado es el mismo `reason`: el contrato no tiene un `duplicateReason` aparte.
    if (confirmDuplicate) dto.confirmDuplicate = true
    this.editSaving = true
    this.editError = ''
    this.apiPaymentService.correctPayment(this.editTarget._id, dto).subscribe((r) => {
      this.editSaving = false
      if (r.error && r.code === 'POSSIBLE_DUPLICATE') {
        //* Evidencia, no veredicto: se pinta el gemelo y la persona decide si guarda de todos modos.
        this.editDuplicate = r.detail ?? { twins: [], activeLeases: 0 }
        return
      }
      if (r.error && r.code === 'PAYMENT_CHANGED') {
        //* Carrera: alguien movió el pago mientras se editaba. Reintentar con los datos viejos volvería
        //* a chocar, así que se cierra y se recarga para que se vuelva a abrir sobre el estado nuevo.
        this.messageService.add({ severity: 'warn', summary: 'El pago cambió', detail: r.msg, life: 8000 })
        this.showEdit = false
        this.reloadAfterEdit()
        return
      }
      if (r.error) {
        //* 400 `PAYMENT_HAS_APPLICATIONS`, «sin cambios» o validación: el mensaje dice qué hacer.
        this.editError = extractApiErrors(r, 'No se pudo corregir el pago.').join(' · ')
        return
      }
      this.showEdit = false
      this.messageService.add({ severity: 'success', summary: 'Pago corregido', detail: 'El cambio quedó en la bitácora del pago.', life: 6000 })
      this.reloadAfterEdit()
    })
  }

  //* El embudo del mes cuenta montos: corregir uno mueve «Entró», no solo la fila.
  private reloadAfterEdit(): void {
    this.tablaPagos?.recargar()
    this.shell.refresh()
  }
}
